import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ChipProps } from '@mui/material';
import strings from 'common/Translation/Translate';
import React, { FC, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import { saBookingLineItemEditPath } from 'utils/constants/routes';
import { URLS } from 'utils/constants/urls';
import { getSaBookingFieldLabel } from './saBookingFieldLabels';

type SaBookingDetailPayload = {
  booking: Record<string, unknown>;
  bookingLineItems: Record<string, unknown>[];
};

type Props = {
  items: Record<string, unknown>[];
  formatFieldValue: (raw: unknown, field: string) => string;
  orderReference: string;
  onLineItemDeletedOrRestored?: (detail: SaBookingDetailPayload, kind: 'delete' | 'restore') => void;
  onLineItemMutationError?: (message: string) => void;
};

function emDash(s: string): string {
  const t = s.trim();
  return t ? t : '—';
}

/** Venue name for the card title; only uses `venueName` from the API (no venue ID fallback). */
function venueNameOnly(li: Record<string, unknown>): string {
  const name = li.venueName != null ? String(li.venueName).trim() : '';
  return name;
}

function timePart(raw: unknown): string {
  if (raw == null || raw === '') return '';
  return String(raw).trim();
}

function eventDateRange(
  startRaw: unknown,
  endRaw: unknown,
  formatFieldValue: (raw: unknown, field: string) => string,
): string {
  const a = formatFieldValue(startRaw, 'startDate').trim();
  const b = formatFieldValue(endRaw, 'endDate').trim();
  if (!a && !b) return '';
  if (a && !b) return a;
  if (!a && b) return b;
  if (a === b) return a;
  return `${a} – ${b}`;
}

function eventTimeRange(startRaw: unknown, endRaw: unknown): string {
  const a = timePart(startRaw);
  const b = timePart(endRaw);
  if (!a && !b) return '';
  if (a && !b) return a;
  if (!a && b) return b;
  if (a === b) return a;
  return `${a} – ${b}`;
}

function lineItemCardHeading(venueName: string, eventSubtype: string, index: number): string {
  const v = venueName.trim();
  const s = eventSubtype.trim();
  if (v && s) return `${v} - ${s}`;
  if (v) return v;
  if (s) return s;
  return `${strings.sa_booking_lookup_li_card_title} ${index + 1}`;
}

type LineItemStatusChip = { label: string; color?: ChipProps['color']; sx?: ChipProps['sx'] };

function lineItemStatusChip(
  li: Record<string, unknown>,
  formatFieldValue: (raw: unknown, field: string) => string,
): LineItemStatusChip {
  if (li.deleted === true) {
    return {
      label: getSaBookingFieldLabel('deleted'),
      sx: theme => ({
        bgcolor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.500',
        color: theme.palette.common.white,
        fontWeight: 600,
      }),
    };
  }

  const raw = li.status;
  const label = formatFieldValue(raw, 'status').trim() || String(raw ?? '').trim() || '—';
  const s = String(raw ?? '').toUpperCase();

  switch (s) {
    case 'CONFIRMED':
    case 'ACTUALIZED':
      return { label, color: 'success' };
    case 'TENTATIVE':
      return { label, color: 'warning' };
    case 'ENQUIRY':
    case 'PENDING':
    case 'WAITLISTED':
      return { label, color: 'info' };
    case 'AWAITING_CONFIRMATION':
      return {
        label,
        sx: {
          bgcolor: '#A3A329',
          color: '#fff',
          fontWeight: 600,
        },
      };
    case 'CANCELLED':
    case 'EXPIRED':
    case 'CANCELLATION_REQUEST':
      return { label, color: 'error' };
    default:
      return {
        label,
        sx: theme => ({
          bgcolor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.500',
          color: theme.palette.common.white,
          fontWeight: 600,
        }),
      };
  }
}

function paxMerged(li: Record<string, unknown>): string {
  const g = Number(li.guaranteedGuests);
  const e = Number(li.expectedGuests);
  const hasG = Number.isFinite(g);
  const hasE = Number.isFinite(e);
  if (!hasG && !hasE) return '';
  const parts: string[] = [];
  if (hasG) {
    parts.push(`${strings.sa_booking_lookup_li_guaranteed_abbr} ${Math.trunc(g)}`);
  }
  if (hasE) {
    parts.push(`${strings.sa_booking_lookup_li_expected_abbr} ${Math.trunc(e)}`);
  }
  return parts.join(' · ');
}

const SectionLabel: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="overline"
    color="text.secondary"
    sx={{
      letterSpacing: '0.08em',
      fontWeight: 700,
      fontSize: '0.65rem',
      lineHeight: 1.5,
      display: 'block',
    }}>
    {children}
  </Typography>
);

export const SuperAdminBookingLineItemCards: FC<Props> = ({
  items,
  formatFieldValue,
  orderReference,
  onLineItemDeletedOrRestored,
  onLineItemMutationError,
}) => {
  const navigate = useNavigate();
  const [deleteDialogLineItemId, setDeleteDialogLineItemId] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [restoreDialogLineItemId, setRestoreDialogLineItemId] = useState<string | null>(null);
  const [restoreReason, setRestoreReason] = useState('');
  const [mutationLoading, setMutationLoading] = useState(false);

  const closeDeleteDialog = useCallback(() => {
    if (!mutationLoading) {
      setDeleteDialogLineItemId(null);
      setDeleteReason('');
    }
  }, [mutationLoading]);

  const closeRestoreDialog = useCallback(() => {
    if (!mutationLoading) {
      setRestoreDialogLineItemId(null);
      setRestoreReason('');
    }
  }, [mutationLoading]);

  const runDelete = useCallback(async () => {
    if (deleteDialogLineItemId == null || !onLineItemDeletedOrRestored) return;
    const reason = deleteReason.trim();
    if (!reason) {
      onLineItemMutationError?.(strings.sa_booking_lookup_li_reason_required);
      return;
    }
    setMutationLoading(true);
    try {
      const { data, status } = await axiosInstance.delete<SaBookingDetailPayload>(
        URLS.superAdminBookingLineItem(deleteDialogLineItemId),
        { data: { updateReason: reason } },
      );
      if (status >= 200 && status < 300 && data) {
        onLineItemDeletedOrRestored(data, 'delete');
        setDeleteDialogLineItemId(null);
        setDeleteReason('');
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; error_description?: string } }; message?: string };
      const msg =
        err.response?.data?.message ??
        err.response?.data?.error_description ??
        err.message ??
        strings.sa_booking_lookup_li_mutation_error;
      onLineItemMutationError?.(msg);
    } finally {
      setMutationLoading(false);
    }
  }, [deleteDialogLineItemId, deleteReason, onLineItemDeletedOrRestored, onLineItemMutationError]);

  const runRestore = useCallback(async () => {
    if (restoreDialogLineItemId == null || !onLineItemDeletedOrRestored) return;
    const reason = restoreReason.trim();
    if (!reason) {
      onLineItemMutationError?.(strings.sa_booking_lookup_li_reason_required);
      return;
    }
    setMutationLoading(true);
    try {
      const { data, status } = await axiosInstance.post<SaBookingDetailPayload>(
        URLS.superAdminBookingLineItemRestore(restoreDialogLineItemId),
        { updateReason: reason },
      );
      if (status >= 200 && status < 300 && data) {
        onLineItemDeletedOrRestored(data, 'restore');
        setRestoreDialogLineItemId(null);
        setRestoreReason('');
      }
    } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string; error_description?: string } }; message?: string };
        const msg =
          err.response?.data?.message ??
          err.response?.data?.error_description ??
          err.message ??
          strings.sa_booking_lookup_li_mutation_error;
        onLineItemMutationError?.(msg);
    } finally {
      setMutationLoading(false);
    }
  }, [restoreDialogLineItemId, restoreReason, onLineItemDeletedOrRestored, onLineItemMutationError]);

  if (!items.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        —
      </Typography>
    );
  }

  return (
    <>
      <Dialog open={deleteDialogLineItemId != null} onClose={closeDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{strings.sa_booking_lookup_li_delete_dialog_title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {strings.sa_booking_lookup_li_delete_dialog_body}
          </Typography>
          <TextField
            label={strings.sa_booking_lookup_li_reason_label}
            value={deleteReason}
            onChange={e => setDeleteReason(e.target.value)}
            fullWidth
            required
            multiline
            minRows={2}
            size="small"
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeleteDialog} disabled={mutationLoading}>
            {strings.sa_booking_lookup_li_delete_cancel}
          </Button>
          <Button onClick={() => void runDelete()} color="error" variant="contained" disabled={mutationLoading}>
            {strings.sa_booking_lookup_li_delete_confirm}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={restoreDialogLineItemId != null} onClose={closeRestoreDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{strings.sa_booking_lookup_li_restore_dialog_title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {strings.sa_booking_lookup_li_restore_dialog_body}
          </Typography>
          <TextField
            label={strings.sa_booking_lookup_li_reason_label}
            value={restoreReason}
            onChange={e => setRestoreReason(e.target.value)}
            fullWidth
            required
            multiline
            minRows={2}
            size="small"
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeRestoreDialog} disabled={mutationLoading}>
            {strings.sa_booking_lookup_li_delete_cancel}
          </Button>
          <Button onClick={() => void runRestore()} variant="contained" disabled={mutationLoading}>
            {strings.sa_booking_lookup_li_restore}
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={2} direction="column" sx={{ mb: 3, display: 'flex' }}>
      {items.map((li, index) => {
        const key = li.id != null ? String(li.id) : `li-${index}`;
        const venueName = venueNameOnly(li);
        const dates = eventDateRange(li.startDate, li.endDate, formatFieldValue);
        const times = eventTimeRange(li.startTime, li.endTime);
        const pax = paxMerged(li);
        const subtype = String(li.eventSubtype ?? '').trim();
        const cardHeading = lineItemCardHeading(venueName, subtype, index);
        const statusChip = lineItemStatusChip(li, formatFieldValue);
        const isDeleted = li.deleted === true;
        const lineItemIdStr = li.id != null ? String(li.id) : '';

        return (
          <Grid item xs={12} key={key} sx={{ width: '100%', maxWidth: '100%' }}>
            <Card
              elevation={0}
              sx={{
                width: '100%',
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                borderLeftWidth: 4,
                borderLeftColor: theme => (isDeleted ? theme.palette.grey[500] : theme.palette.primary.main),
                bgcolor: theme =>
                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : theme.palette.background.paper,
                boxShadow: theme =>
                  theme.palette.mode === 'dark'
                    ? '0 1px 3px rgba(0,0,0,0.35)'
                    : '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
                transition: 'box-shadow 0.2s ease',
                '&:hover': {
                  boxShadow: theme =>
                    theme.palette.mode === 'dark'
                      ? '0 4px 12px rgba(0,0,0,0.45)'
                      : '0 4px 14px rgba(0,0,0,0.08)',
                },
              }}>
              <CardContent
                sx={{
                  p: 2.5,
                  '&:last-child': { pb: 2.5 },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'flex-start' },
                    justifyContent: 'space-between',
                    gap: 1.5,
                  }}>
                  <Typography
                    variant="subtitle1"
                    component="h3"
                    color="primary"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: 0.15,
                      lineHeight: 1.35,
                      fontSize: { xs: '1.05rem', sm: '1.2rem' },
                      pr: { sm: 1 },
                      minWidth: 0,
                      flex: '1 1 auto',
                    }}>
                    {cardHeading}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 1,
                      flexShrink: 0,
                    }}>
                    <Chip
                      label={statusChip.label}
                      color={statusChip.color}
                      size="medium"
                      sx={{
                        fontWeight: 600,
                        ...statusChip.sx,
                      }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      <Tooltip title={strings.sa_booking_lookup_li_edit}>
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label={strings.sa_booking_lookup_li_edit}
                            onClick={() => {
                              if (li.id != null && orderReference) {
                                navigate(saBookingLineItemEditPath(orderReference, String(li.id)));
                              }
                            }}
                            disabled={li.id == null || !orderReference || mutationLoading}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {isDeleted ? (
                        <Tooltip title={strings.sa_booking_lookup_li_restore}>
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label={strings.sa_booking_lookup_li_restore}
                              onClick={() => {
                                if (lineItemIdStr) {
                                  setRestoreReason('');
                                  setRestoreDialogLineItemId(lineItemIdStr);
                                }
                              }}
                              disabled={!lineItemIdStr || !onLineItemDeletedOrRestored || mutationLoading}>
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title={strings.sa_booking_lookup_li_delete}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              aria-label={strings.sa_booking_lookup_li_delete}
                              onClick={() => {
                                if (lineItemIdStr) {
                                  setDeleteReason('');
                                  setDeleteDialogLineItemId(lineItemIdStr);
                                }
                              }}
                              disabled={!lineItemIdStr || !onLineItemDeletedOrRestored || mutationLoading}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Box>

                <Divider />

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                    gap: 2,
                    alignItems: 'flex-start',
                  }}>
                  <Box>
                    <SectionLabel>{strings.sa_booking_lookup_li_event_date}</SectionLabel>
                    <Typography variant="body2" sx={{ mt: 0.75, fontWeight: 500, color: 'text.primary' }}>
                      {emDash(dates)}
                    </Typography>
                  </Box>
                  <Box>
                    <SectionLabel>{strings.sa_booking_lookup_li_event_time}</SectionLabel>
                    <Typography variant="body2" sx={{ mt: 0.75, fontWeight: 500, color: 'text.primary' }}>
                      {emDash(times)}
                    </Typography>
                  </Box>
                  <Box>
                    <SectionLabel>{strings.sa_booking_lookup_li_pax}</SectionLabel>
                    <Typography variant="body2" sx={{ mt: 0.75, fontWeight: 500, color: 'text.primary' }}>
                      {emDash(pax)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
      </Grid>
    </>
  );
};
